import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Ticket, Check, Copy, XCircle, Mail, Inbox, Clock } from 'lucide-react';
import { formatMinutes } from '../lib/time';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_NAMES = { pack5: 'Pack 5 clases', pack10: 'Pack 10 clases', dele_pack5: 'Bono DELE 5 clases' };
const STATUS = {
  pending: { label: 'Pendiente de pago', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  active: { label: 'Activo', cls: 'bg-green-50 text-green-700 border-green-200' },
  cancelled: { label: 'Cancelado', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export default function AdminPacks({ onSessionExpired }) {
  const { token } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const fetchPacks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/packs`, authHeaders);
      setPacks(data);
    } catch (e) {
      if (e.response?.status === 401) onSessionExpired && onSessionExpired();
      else toast.error('Error al cargar los packs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activate = async (p) => {
    if (!window.confirm(`¿Activar el ${PACK_NAMES[p.pack_type]} de ${p.name}?\n\nSe enviará a ${p.email} su enlace privado para reservar las clases.`)) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/packs/${p.id}/activate`, {}, authHeaders);
      toast.success('Pack activado. El alumno ha recibido su enlace por email.');
      fetchPacks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al activar el pack');
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (p) => {
    if (!window.confirm(`¿Cancelar el pack de ${p.name}? No podrá reservar más clases con su enlace.`)) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/packs/${p.id}/cancel`, {}, authHeaders);
      toast.success('Pack cancelado');
      fetchPacks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar');
    } finally {
      setBusyId(null);
    }
  };

  const toggleHalfHour = async (p) => {
    setBusyId(p.id);
    try {
      await axios.patch(`${API}/packs/${p.id}/half-hour`, { enabled: !p.allow_half_hour }, authHeaders);
      toast.success(!p.allow_half_hour
        ? 'Este bono ya puede reservarse en clases de 30 o 60 minutos'
        : 'Este bono vuelve a reservarse solo en clases de 60 minutos');
      fetchPacks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar la opción');
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(p.pack_url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#4A4A4A]">Packs de clases</h2>
          <p className="text-sm text-gray-500">{packs.length} pack{packs.length === 1 ? '' : 's'} en total</p>
        </div>
      </div>

      {packs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-packs">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Todavía no hay packs reservados.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {packs.map((p, index) => {
            const st = STATUS[p.status] || STATUS.pending;
            return (
              <div key={p.id} data-testid={`pack-card-${index}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <span className="w-10 h-10 bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] rounded-full flex items-center justify-center text-white">
                        <Ticket className="w-5 h-5" />
                      </span>
                      <span className="font-bold text-[#4A4A4A] text-lg">{p.name}</span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-[#E8F5FA] text-[#7DBDD9] border-[#A8D8EA]">
                        {PACK_NAMES[p.pack_type] || p.pack_type}
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${st.cls}`} data-testid={`pack-status-${index}`}>
                        {st.label}
                      </span>
                      <span className="px-2 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-600" data-testid={`pack-sessions-${index}`}>
                        {p.allow_half_hour
                          ? `${formatMinutes(p.completed_minutes)} consumidos · ${formatMinutes(p.scheduled_minutes)} programados · ${formatMinutes(p.remaining_minutes)} por reservar`
                          : `${p.used_sessions}/${p.total_sessions} usadas · ${p.remaining_sessions} libres`}
                      </span>
                      {p.allow_half_hour && (
                        <span className="px-2 py-1 rounded-md text-xs font-semibold border bg-[#FFF6E5] text-[#D9903A] border-[#F0C987]" data-testid={`pack-half-hour-badge-${index}`}>
                          30 o 60 min
                        </span>
                      )}
                    </div>
                    <a href={`mailto:${p.email}`} className="text-sm text-gray-600 hover:text-[#7DBDD9] flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />{p.email}
                    </a>
                    {p.sessions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.sessions.map((s) => (
                          <span key={s.id} className="px-2.5 py-1 rounded-lg bg-[#E8F5FA] text-xs font-semibold text-[#4A4A4A]">
                            {s.date} · {s.time} ({s.duration_minutes || 60} min)
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap md:justify-end gap-2">
                    {p.status !== 'active' && p.status !== 'cancelled' && (
                      <button
                        onClick={() => activate(p)}
                        disabled={busyId === p.id}
                        data-testid={`pack-activate-${index}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Activar pack (pago recibido)
                      </button>
                    )}
                    {p.status !== 'cancelled' && (
                      <button
                        onClick={() => toggleHalfHour(p)}
                        disabled={busyId === p.id}
                        data-testid={`pack-half-hour-toggle-${index}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                          p.allow_half_hour
                            ? 'bg-[#FFF6E5] hover:bg-[#F0C987]/40 text-[#D9903A]'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                      >
                        <Clock className="w-3.5 h-3.5" />
                        {p.allow_half_hour ? 'Quitar clases de 30 min' : 'Permitir clases de 30 min'}
                      </button>
                    )}
                    <button
                      onClick={() => copyLink(p)}
                      data-testid={`pack-copy-${index}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8F5FA] hover:bg-[#A8D8EA]/40 text-[#7DBDD9] text-xs font-semibold transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copiar enlace del alumno
                    </button>
                    {p.status !== 'cancelled' && (
                      <button
                        onClick={() => cancel(p)}
                        disabled={busyId === p.id}
                        data-testid={`pack-cancel-${index}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
